import {ExclamationTriangleIcon} from "@heroicons/react/20/solid";
import {OtsActionButton} from "../components/OtsButton";
import type {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {OtsDialog} from "../components/OtsDialog";
import {TicketCostPanel, TicketCostPanelProps} from "./TicketCostPanel";

export type TicketCostDialogProps = TicketCostPanelProps & {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  loading?: boolean;
  refreshing?: boolean;
  error?: string;
  notice?: string;
  onRetry?: () => void;
  onOpenLegacy?: () => void;
};

/** Full ticket-cost dialog host, including versions, inputs, allocations, billing context, and invoices. */
export const TicketCostDialog = ({
  open,
  onClose,
  title = "Cost details",
  description,
  brand = "water-go",
  colorScheme = "system",
  loading = false,
  refreshing = false,
  error,
  notice,
  onRetry,
  onOpenLegacy,
  ...panelProps
}: TicketCostDialogProps) => (
  <OtsDialog open={open} onClose={onClose} title={title} description={description} truncateDescription
    brand={brand} colorScheme={colorScheme} compact={false}>
    {loading ? (
      <div className="flex min-h-48 items-center justify-center rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-sm text-[var(--ots-text-muted)]">
        <span className="mr-3 size-5 animate-spin rounded-full border-2 border-[var(--ots-outline)] border-t-[var(--ots-primary)]" aria-hidden="true"/>
        Calculating ticket costs…
      </div>
    ) : error ? (
      <div className="rounded-xl border border-[var(--ots-error)]/30 bg-[var(--ots-error-container)] p-5 text-sm text-[var(--ots-on-error-container)]">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0"/>
          <div>
            <h2 className="font-semibold">Cost details are unavailable</h2>
            <p className="mt-1 text-xs">{error}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {onRetry && <OtsActionButton size="compact" onClick={onRetry}>Try again</OtsActionButton>}
          {onOpenLegacy && <OtsActionButton size="compact" variant="secondary" onClick={onOpenLegacy}>Open legacy view</OtsActionButton>}
        </div>
      </div>
    ) : (
      <>
        {refreshing && (
          <div role="status" className="mb-3 flex items-center gap-2 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-3 py-2 text-xs text-[var(--ots-text-muted)]">
            <span className="size-3.5 animate-spin rounded-full border-2 border-[var(--ots-outline)] border-t-[var(--ots-primary)]" aria-hidden="true"/>
            Refreshing current, preview, and historical costs…
          </div>
        )}
        {notice && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-[var(--ots-warning)]/30 bg-[var(--ots-warning-container)] px-3 py-2 text-xs text-[var(--ots-on-warning-container)]">
            <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0"/>
            {notice}
          </div>
        )}
        <TicketCostPanel {...panelProps}/>
      </>
    )}
  </OtsDialog>
);
